import type { UiNode } from "@agent/uiSchema";
import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components } from "@api/schema";
import type { EntityKind } from "@entities/entitiesClient";

/** Mirrors brian's `ViewEntityOut`; shaped like `Entity` so the search palette renders either. */
export interface ViewEntity {
  id: string;
  kind: EntityKind;
  name: string | null;
  clientName: string | null;
}

export interface View {
  id: string;
  /** The document kind, shared across views; see `viewCaption`. */
  name: string;
  /** Null on documents a patient or client only ever has one of. */
  subtitle: string | null;
  /** Who/what the view is about; `[]` only if every linked entity vanished. */
  entities: ViewEntity[];
  root: UiNode;
  data: Record<string, unknown>;
  /** Set on a visit's stage note; only such a view can be verified. */
  visitStageId: string | null;
  /** A vet's verification locks the view; null until then. */
  reviewedAt: string | null;
  reviewedByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export const toView = (v: components["schemas"]["ViewOut"]): View => ({
  id: v.uuid,
  name: v.title,
  subtitle: v.subtitle ?? null,
  // Defaulted despite the type so an older brian (deploy order, rollback) can't throw here.
  entities: (v.entities ?? []).map((s) => ({
    id: s.entity_uuid,
    kind: s.kind,
    name: s.name,
    clientName: s.client_name ?? null,
  })),
  root: v.root as UiNode,
  data: (v.data as Record<string, unknown> | null) ?? {},
  visitStageId: v.visit_stage_uuid ?? null,
  reviewedAt: v.reviewed_at ?? null,
  reviewedByName: v.reviewed_by_name ?? null,
  createdAt: v.created_at,
  updatedAt: v.updated_at,
});

export const viewsClient = {
  list: (anchor?: string): Promise<View[]> =>
    request(() =>
      apiClient.GET("/views", {
        params: { query: anchor ? { anchor } : {} },
      }),
    ).then((rows) => rows.map(toView)),
  review: (id: string): Promise<View> =>
    request(() =>
      apiClient.POST("/views/{view_uuid}/review", {
        params: { path: { view_uuid: id } },
      }),
    ).then(toView),
};
